import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Money, Bank, Receipt, Warning, CheckCircle, Circle, ArrowRight, Plus,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { RM, RMcompact, MONTHS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const STEPS = ["Attendance", "Calculation", "Review", "Approval", "Disbursement"];
const STATUS_STEP = { draft: 1, review: 2, approved: 3, disbursed: 5 };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <Layout title="Payroll Command Centre">
        <div className="h-64 animate-pulse rounded-md bg-[#FFFFFF]" />
      </Layout>
    );

  const run = data.latest_run;
  const sum = data.latest_summary;
  const currentStep = run ? STATUS_STEP[run.status] || 1 : 0;

  const grossVal = sum ? sum.gross : data.estimated_gross;
  const netVal = sum ? sum.net_pay : data.estimated_net;
  const statVal = sum ? sum.statutory_total : data.estimated_statutory;

  return (
    <Layout
      title="Payroll Command Centre"
      subtitle="Good day. Here is your current payroll position."
      actions={
        isAdmin ? (
          <Button
            data-testid="start-payroll-btn"
            onClick={() => navigate("/payroll")}
            className="bg-red-500 font-semibold text-white transition-colors duration-200 hover:bg-red-600"
          >
            <Plus size={16} weight="bold" className="mr-1.5" /> Start Payroll Run
          </Button>
        ) : null
      }
    >
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-employees" label="Total Employees" value={data.total_employees}
          sub={`${data.active_employees} active · ${data.new_this_month} new this month`} icon={Users} tone="white" delay={0} />
        <StatCard testId="stat-net" label={sum ? "Net Payroll" : "Est. Net Payroll"} value={RMcompact(netVal)}
          sub={`Gross ${RMcompact(grossVal)}`} icon={Money} tone="amber" delay={60} />
        <StatCard testId="stat-statutory" label="Statutory Deductions" value={RMcompact(statVal)}
          sub="EPF · SOCSO · EIS · PCB" icon={Receipt} tone="blue" delay={120} />
        <StatCard testId="stat-employer" label="Employer Cost" value={RMcompact(sum ? sum.employer_cost : grossVal * 1.15)}
          sub="Incl. employer contributions" icon={Bank} tone="green" delay={180} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Payroll progress */}
        <div className="animate-fade-up border border-[#E2E5EA] bg-[#FFFFFF] p-6 lg:col-span-2" style={{ animationDelay: "220ms" }}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#0F172A]">
                {run ? `${MONTHS[run.month]} ${run.year} Payroll Run` : "No active payroll run"}
              </h3>
              <p className="mt-0.5 text-sm text-[#9CA3AF]">
                {run ? `Pay date: ${run.pay_date}` : "Start a run to begin processing this month."}
              </p>
            </div>
            {run ? <StatusBadge status={run.status} testId="run-status-badge" /> : null}
          </div>

          <div className="mt-8 flex items-center justify-between">
            {STEPS.map((label, i) => {
              const idx = i + 1;
              const done = idx < currentStep;
              const active = idx === currentStep;
              return (
                <div key={label} className="flex flex-1 flex-col items-center">
                  <div className="flex w-full items-center">
                    {i > 0 && <div className={`h-0.5 flex-1 ${idx <= currentStep ? "bg-red-500" : "bg-[#E2E5EA]"}`} />}
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 ${
                        done ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                        : active ? "border-red-500 bg-red-500/10 text-red-500"
                        : "border-[#E2E5EA] bg-[#F4F5F7] text-[#6B7280]"
                      }`}
                    >
                      {done ? <CheckCircle size={18} weight="fill" /> : <Circle size={12} weight={active ? "fill" : "regular"} />}
                    </div>
                    {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${idx < currentStep ? "bg-red-500" : "bg-[#E2E5EA]"}`} />}
                  </div>
                  <span className={`mt-2 text-center text-[11px] ${active ? "text-red-500" : done ? "text-emerald-400" : "text-[#6B7280]"}`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mt-8">
            <Button
              variant="outline"
              data-testid="dashboard-view-payroll"
              onClick={() => (run ? navigate(`/payroll/${run.id}`) : navigate("/payroll"))}
              className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-red-500"
            >
              {run ? "View payroll details" : "Go to payroll"} <ArrowRight size={15} className="ml-1.5" />
            </Button>
          </div>
        </div>

        {/* Needs attention */}
        <div className="animate-fade-up border border-[#E2E5EA] bg-[#FFFFFF] p-6" style={{ animationDelay: "280ms" }}>
          <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Needs Attention</h3>
          <div className="mt-4 space-y-3">
            {data.needs_attention.length === 0 ? (
              <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-400">
                <CheckCircle size={18} weight="fill" /> All records look good.
              </div>
            ) : (
              data.needs_attention.map((n) => (
                <button
                  key={n.type}
                  data-testid={`attention-${n.type}`}
                  onClick={() => navigate("/employees")}
                  className="flex w-full items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-3 text-left transition-colors duration-200 hover:bg-red-500/20"
                >
                  <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-red-400" />
                  <div>
                    <p className="text-sm font-medium text-red-300">
                      {n.label} <span className="font-bold">({n.count})</span>
                    </p>
                    <p className="text-xs text-red-400/70">{n.detail}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
